import { useEffect, useMemo, useRef } from 'react'
import { PointsBadge } from '@/components/PointsBadge'
import { useTranslation } from '@/i18n'
import type {
  FootballMatch,
  MatchListStatus,
  PaginatedMatchesResponse,
  PredictionStageContextDto,
} from '@/features/poules/api'
import {
  formatKickoffDateHeading,
  formatKickoffDateTime,
  formatKickoffShortDate,
  formatKickoffTime,
  getKickoffDateKey,
  getKickoffTimeValue,
} from '@/lib/kickoff'

export interface MatchCardAction {
  kind: 'predict' | 'detail'
  match: FootballMatch
}

export function PouleMatchesTab({
  status,
  pages,
  isLoading,
  isError,
  isFetchingNextPage,
  hasNextPage,
  onRetry,
  onLoadMore,
  onSelectMatch,
}: {
  status: MatchListStatus
  pages: PaginatedMatchesResponse[] | undefined
  isLoading: boolean
  isError: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  onRetry: () => void
  onLoadMore: () => void
  onSelectMatch: (action: MatchCardAction) => void
}) {
  const { t } = useTranslation()
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const matches = useMemo(() => {
    const items = (pages ?? []).flatMap((page) => page.data)

    return [...items].sort((left, right) => {
      const leftValue = getKickoffTimeValue(left.kickoff_utc, left.kickoff_nl)
      const rightValue = getKickoffTimeValue(right.kickoff_utc, right.kickoff_nl)
      return status === 'future' ? leftValue - rightValue : rightValue - leftValue
    })
  }, [pages, status])
  const groupedMatches = useMemo(() => groupMatchesByDay(matches), [matches])
  const stageContext = pages?.[0]?.predictionStageContext

  useEffect(() => {
    const node = sentinelRef.current
    if (!node || !hasNextPage || isFetchingNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMore()
        }
      },
      { rootMargin: '280px 0px' },
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, onLoadMore])

  if (isLoading) {
    return (
      <section className="rounded-[1.35rem] border border-[#202427] bg-[#0a0d0f] p-5" role="status" aria-live="polite">
        <h2 className="text-xl font-black tracking-tight text-white">
          {status === 'future' ? t('Wedstrijden laden') : t('Uitslagen laden')}
        </h2>
        <p className="mt-2 text-sm text-surface-400">
          {status === 'future'
            ? t('We halen je komende wedstrijden en prediction-state op.')
            : t('We halen de gespeelde wedstrijden en behaalde punten op.')}
        </p>
      </section>
    )
  }

  if (isError) {
    return (
      <section className="rounded-[1.35rem] border border-red-900/60 bg-red-950/25 p-5 text-red-100" role="alert">
        <h2 className="text-xl font-black tracking-tight">
          {status === 'future' ? t('Wedstrijden niet geladen') : t('Uitslagen niet geladen')}
        </h2>
        <p className="mt-2 text-sm text-red-100/80">
          {t('Probeer de tab opnieuw te laden. De backendresponse was niet bruikbaar.')}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 rounded-full border border-red-200/20 px-4 py-2 text-sm font-semibold transition hover:bg-white/5"
        >
          {t('Opnieuw proberen')}
        </button>
      </section>
    )
  }

  if (matches.length === 0) {
    return (
      <section className="rounded-[1.35rem] border border-[#202427] bg-[#0a0d0f] p-5">
        <h2 className="text-xl font-black tracking-tight text-white">
          {status === 'future' ? t('Geen komende wedstrijden') : t('Nog geen uitslagen')}
        </h2>
        <p className="mt-2 text-sm text-surface-400">
          {status === 'future'
            ? t('Er staan nu geen toekomstige wedstrijden klaar in deze poule.')
            : t('Er zijn nog geen gespeelde wedstrijden om te tonen.')}
        </p>
      </section>
    )
  }

  return (
    <div className="space-y-5">
      {status === 'future' ? <PredictionStageBanner context={stageContext} /> : null}

      <div className="space-y-5">
        {groupedMatches.map((group) => (
          <section key={group.key} className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-[#20282b]" />
              <p className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
                {group.label}
              </p>
              <div className="h-px flex-1 bg-[#20282b]" />
            </div>

            <div className="space-y-3">
              {group.matches.map((match) => (
                <PouleMatchCard
                  key={match.id}
                  match={match}
                  mode={status}
                  onClick={() =>
                    onSelectMatch({
                      kind: status === 'future' && canPredictMatch(match) ? 'predict' : 'detail',
                      match,
                    })
                  }
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      <div ref={sentinelRef} />

      {hasNextPage ? (
        <div className="flex justify-center">
          <button
          type="button"
          onClick={onLoadMore}
          disabled={isFetchingNextPage}
            className="rounded-full border border-[#30383c] bg-[#0d1113] px-4 py-2 text-sm font-semibold text-surface-200 transition hover:bg-[#151a1c] focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0d0f] disabled:opacity-50"
          >
            {isFetchingNextPage ? t('Meer laden…') : t('Meer laden')}
          </button>
        </div>
      ) : null}
    </div>
  )
}

export function PouleMatchCard({
  match,
  mode,
  onClick,
}: {
  match: FootballMatch
  mode: MatchListStatus
  onClick: () => void
}) {
  const { t } = useTranslation()
  const scoreState = getScoreState(match, t)
  const isFinal = match.status === 'ft'
  const isLive = match.status === 'live' || match.status === 'ht'
  const isHalfTime = match.status === 'ht'
  const isCancelled = match.status === 'cancelled'
  const isPostponed = match.status === 'postponed'
  const showTbd = match.kickoff_time_tbd && !isFinal && !isLive && !isCancelled && !isPostponed
  const showBottomStatus = mode === 'future' || (!isLive && !isFinal)
  const hasActualScore =
    typeof match.score?.home === 'number' || typeof match.score?.away === 'number'
  const hasPrediction =
    typeof match.user_prediction?.home_pred === 'number' ||
    typeof match.user_prediction?.away_pred === 'number'
  const showSecondaryScore = mode === 'future' ? hasActualScore : hasPrediction
  const homeValue =
    mode === 'future'
      ? formatPredictionValue(match.user_prediction?.home_pred)
      : scoreState.home
  const awayValue =
    mode === 'future'
      ? formatPredictionValue(match.user_prediction?.away_pred)
      : scoreState.away
  const homeSecondaryValue = showSecondaryScore
    ? mode === 'future'
      ? formatScoreValue(match.score?.home)
      : formatPredictionValue(match.user_prediction?.home_pred)
    : undefined
  const awaySecondaryValue = showSecondaryScore
    ? mode === 'future'
      ? formatScoreValue(match.score?.away)
      : formatPredictionValue(match.user_prediction?.away_pred)
    : undefined
  const primaryScoreLabel = mode === 'future' ? t('Voorspelling') : t('Uitslag')
  const secondaryScoreLabel = mode === 'future' ? t('Uitslag') : t('Voorspelling')

  return (
    <button
      type="button"
      onClick={onClick}
      className="group block w-full rounded-[1rem] border border-white/[0.06] bg-[#0b0f10] px-4 py-3 text-left transition hover:border-surface-600 hover:bg-[#151a1c] focus-visible:border-surface-500 focus-visible:bg-[#151a1c] focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0d0f]"
    >
      <div className="flex items-start gap-3">
        <div
          className={[
            'shrink-0 pt-0.5 text-center',
            showTbd ? 'w-[5.25rem]' : 'w-14',
          ].join(' ')}
        >
          {isLive ? (
            <>
              <span className="inline-flex rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-red-200">
                {isHalfTime ? t('Rust') : t('Live')}
              </span>
              <p className="mt-1.5 text-[11px] font-semibold text-white">
                {isHalfTime ? "45'" : getMinuteLabel(match, t)}
              </p>
            </>
          ) : isPostponed || isCancelled ? (
            <span className="text-xs font-bold uppercase tracking-[0.12em] text-surface-300">
              {t('TBD')}
            </span>
          ) : showTbd ? (
            <>
              <p className="text-[11px] font-bold text-white">
                {formatKickoffShortDate(match.kickoff_utc, match.kickoff_nl)}
              </p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.12em] text-surface-500">
                {t('TBD')}
              </p>
            </>
          ) : (
            <p className="text-xs font-bold text-white">
              {isFinal ? t('FT') : formatKickoffTime(match.kickoff_utc, match.kickoff_nl)}
            </p>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex justify-end gap-2 pr-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-500">
            <span className="min-w-[2rem] text-right">{primaryScoreLabel}</span>
            {showSecondaryScore ? (
              <span className="min-w-[4.25rem] text-right">{secondaryScoreLabel}</span>
            ) : null}
          </div>
          <div className="space-y-2.5">
            <TeamRow
              team={match.home_team}
              value={homeValue}
              secondaryValue={homeSecondaryValue}
              pointsBadgeValue={mode === 'past' ? (match.user_prediction?.points_awarded ?? 0) : undefined}
            />
            <TeamRow
              team={match.away_team}
              value={awayValue}
              secondaryValue={awaySecondaryValue}
            />
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {match.stage_name ? <MetaPill label={match.stage_name} /> : null}
            {mode === 'future' && (match.user_prediction?.home_pred == null || match.user_prediction?.away_pred == null) ? (
              canPredictMatch(match) ? null : <MetaPill label={t('Gesloten')} tone="muted" />
            ) : mode !== 'future' && showBottomStatus ? (
              <MetaPill label={getPastStatusLabel(match, t)} tone="muted" />
            ) : null}
            {match.joker ? (
              <MetaPill
                label={
                  typeof match.joker_multiplier === 'number' && match.joker_multiplier > 1
                  ? t('Joker x{multiplier}', { multiplier: match.joker_multiplier })
                    : t('Joker')
                }
              />
            ) : null}
            {mode === 'future' && match.prediction_locked_by_stage_release ? (
              <MetaPill label={t('Fase vergrendeld')} tone="muted" />
            ) : null}
          </div>
        </div>
      </div>

      {mode === 'future' &&
      !match.is_predictable &&
      match.prediction_locked_by_stage_release ? (
        <div className="mt-3.5 rounded-[0.95rem] border border-amber-900/40 bg-amber-950/20 px-3.5 py-3 text-sm text-amber-100">
          {match.prediction_locked_reason ||
            getStageLockCopy(t, match.prediction_lock_context?.current_stage_name)}
        </div>
      ) : null}
    </button>
  )
}

function TeamRow({
  team,
  value,
  secondaryValue,
  pointsBadgeValue,
}: {
  team: FootballMatch['home_team']
  value: string
  secondaryValue?: string | undefined
  pointsBadgeValue?: number | undefined
}) {
  return (
    <div className="flex items-center gap-3">
      <TeamLogo team={team} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-white sm:text-base">{team.name}</p>
      </div>
      {typeof pointsBadgeValue === 'number' ? <PointsBadge points={pointsBadgeValue} /> : null}
      <span className="min-w-[2rem] text-right text-base font-black text-white">{value}</span>
      {secondaryValue !== undefined ? (
        <span className="min-w-[4.25rem] text-right text-xs font-semibold text-surface-400">
          {secondaryValue}
        </span>
      ) : null}
    </div>
  )
}

function TeamLogo({ team }: { team: FootballMatch['home_team'] }) {
  if (team.logo_url) {
    return (
      <img
        src={team.logo_url}
        alt=""
        className="h-8 w-8 shrink-0 object-contain"
      />
    )
  }

  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-surface-800 text-[10px] font-black uppercase text-surface-300">
      {getInitials(team.name)}
    </span>
  )
}

function PredictionStageBanner({
  context,
}: {
  context: PredictionStageContextDto | null | undefined
}) {
  const { t } = useTranslation()
  if (!context) return null

  return (
    <section className="rounded-[1.2rem] border border-[#202427] bg-[#0a0d0f] p-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
        {t('Prediction stage')}
      </p>
      <h2 className="mt-2 text-lg font-black text-white">
        {context.active_stage_name || t('Actieve fase')}
      </h2>
      <p className="mt-1 text-sm text-surface-400">
        {context.predictions_open
          ? t('Voorspellingen staan open voor deze fase.')
          : t('Voorspellingen zijn voor deze fase op dit moment gesloten.')}
      </p>
      {context.stage_lock_starts_at_utc || context.stage_lock_starts_at_nl ? (
        <p className="mt-2 text-xs text-surface-500">
          {t('Stage lock')}: {formatKickoffDateTime(
            context.stage_lock_starts_at_utc,
            context.stage_lock_starts_at_nl,
          )}
        </p>
      ) : null}
    </section>
  )
}

function MetaPill({
  label,
  tone = 'neutral',
}: {
  label: string
  tone?: 'neutral' | 'action' | 'muted'
}) {
  const className =
    tone === 'action'
      ? 'border-[#203841] bg-[#10191c] text-[#93d9ff] transition-colors duration-200 group-hover:border-accent-400/60 group-hover:bg-accent-500 group-hover:text-white'
      : tone === 'muted'
        ? 'border-surface-700 bg-surface-900 text-surface-400'
        : 'border-[#202427] bg-[#0d1113] text-surface-300'

  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${className}`}
    >
      {label}
    </span>
  )
}

function groupMatchesByDay(matches: FootballMatch[]) {
  const map = new Map<string, { key: string; label: string; matches: FootballMatch[] }>()

  for (const match of matches) {
    const key = getKickoffDateKey(match.kickoff_utc, match.kickoff_nl)
    const label = formatKickoffDateHeading(match.kickoff_utc, match.kickoff_nl)
    const group = map.get(key)

    if (group) {
      group.matches.push(match)
    } else {
      map.set(key, { key, label, matches: [match] })
    }
  }

  return Array.from(map.values())
}

export function canPredictMatch(match: FootballMatch) {
  if (match.status === 'cancelled' || match.status === 'postponed') return false
  if (match.is_predictable) return true
  return match.predict_status === 'open' || match.predict_status === 'mixed'
}

type Translate = (key: string, values?: Record<string, string | number>) => string

function getScoreState(match: FootballMatch, t: Translate) {
  if (match.status === 'cancelled') {
    return {
      primary: t('Geannuleerd'),
      secondary: t('Deze wedstrijd gaat niet door.'),
      home: '–',
      away: '–',
    }
  }

  if (match.status === 'postponed') {
    return {
      primary: t('Uitgesteld'),
      secondary: t('Nieuwe speeldatum volgt.'),
      home: '–',
      away: '–',
    }
  }

  if (match.status === 'live' || match.status === 'ht' || match.status === 'ft') {
    return {
      primary: `${formatScoreValue(match.score?.home)} - ${formatScoreValue(match.score?.away)}`,
      secondary:
        match.status === 'ft'
          ? t('Definitieve uitslag')
          : match.status === 'ht'
            ? t('Ruststand')
            : getMinuteLabel(match, t),
      home: formatScoreValue(match.score?.home),
      away: formatScoreValue(match.score?.away),
    }
  }

  return {
    primary: match.kickoff_time_tbd ? t('Tijd volgt') : formatKickoffTime(match.kickoff_utc, match.kickoff_nl),
    secondary: t('Nog niet gespeeld'),
    home: '•',
    away: '•',
  }
}

function getPastStatusLabel(match: FootballMatch, t: Translate) {
  if (match.status === 'ft') return t('Gespeeld')
  if (match.status === 'live') return t('Live')
  if (match.status === 'ht') return t('Rust')
  if (match.status === 'cancelled') return t('Geannuleerd')
  if (match.status === 'postponed') return t('Uitgesteld')
  return t('Ingepland')
}

function getStageLockCopy(t: Translate, stageName?: string | null | undefined) {
  return stageName
    ? t('Voorspellen is gelockt door de stage release van {stageName}.', { stageName })
    : t('Voorspellen is tijdelijk gelockt door de stage release.')
}

function getMinuteLabel(match: FootballMatch, t: Translate) {
  if (typeof match.minute !== 'number') return t('Live')
  if (typeof match.extra_minute === 'number' && match.extra_minute > 0) {
    return `${match.minute}+${match.extra_minute}'`
  }
  return `${match.minute}'`
}

function formatScoreValue(value?: number | null) {
  return typeof value === 'number' ? String(value) : '–'
}

function formatPredictionValue(value?: number | null) {
  return typeof value === 'number' ? String(value) : '–'
}

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}
